// ORDER is not BILLING.
//
// Every price in this system has assumed that the thing you ORDER and the
// thing the vendor BILLS are the same thing: one row, one `purchase_unit`, one
// `package_price`, and the invoice is `packages × package_price`. For a case of
// buns that is true. For a side of beef it is not, and the gap is where the
// 2026-08-14 ribeye failure lives:
//
//   BEEF RIBEYE BNLS LIPON PRIME · "4 PC / AVG 60 LB" · Order Unit LB
//   Qty 1 LB · Price/Unit $16.40 · Line Total $16.40
//
// while the Savings Review understood need ~23 LB, receive ~60 LB, extra ~37 LB.
// Both cannot be true. What Sysco actually sells is ONE CASE holding four
// whole ribeyes weighing about sixty pounds between them, invoiced at $16.40
// for every pound that turns up on the truck — about $984, exactly none of it
// knowable before the meat is weighed. `purchase_unit = 'LB'` was the catalog
// answering "what do you bill in?" into a column that asks "what do you order?".
//
// Two shapes of that same corruption are live in production (2026-08-09):
//
//   store 373  qty_per_package = 1   price 16.40 → "order 1 LB for $16.40"
//   store 383  qty_per_package = 60  price 16.40 → "$0.27/LB prime ribeye"
//
// The first under-orders by a factor of sixty; the second makes prime beef the
// cheapest protein in the catalog and would win every price comparison it
// entered. One model fixes both, because both are the same mistake.
//
// ── What this module is ──────────────────────────────────────────────────────
//
// The ONE place that separates the two questions, for both runtimes:
//
//   ORDER UNIT    what you say to the vendor            "1 case"
//   BILLING UNIT  what the invoice multiplies           "$16.40 × 60 LB"
//
// and names which of four fulfillment models a row follows. It computes; it
// never decides to write. Classification is EVIDENCE-DRIVEN from facts already
// stored on the row — the explicit billing columns first, then the pack facts
// the parser froze into `conversion_snapshot`, then the purchase unit itself.
// Where the evidence does not settle the question it returns `unknown` and
// says why. It never guesses a model, because every model here changes money.
//
// ── Deliberate non-dependencies ──────────────────────────────────────────────
//
// Pure ESM, no Deno APIs, no I/O, and it never throws — every export returns a
// well-formed object for any input, including null. It imports exactly one
// thing, `convertBasisQuantity` from packageBasis.mjs, which is itself
// import-free; that keeps the browser mirror (`app/javascript/utils/
// billingBasis.js`) a true re-export with no new bundle weight.
//
// It does NOT import `packText.mjs`. That module is the single reader of pack
// strings (AGENTS.md, "One pack string, one meaning") and it reaches
// `menu-recipe-costing/units.mjs`, a 60 KB conversion table the browser is
// deliberately kept away from (see `utils/countUnitModel.js`). So this module
// never parses a pack string. It reads the parser's ALREADY-STORED output, and
// a caller that legitimately owns the parser — the audit script, an edge
// function — may hand its reading in as `packFacts`. Same facts, same
// arithmetic, one parser.

import { convertBasisQuantity } from './packageBasis.mjs';

/**
 * The four ways a vendor turns an order into an invoice. These are exactly the
 * values `purchase_options.fulfillment_model` accepts (migration
 * 20260814120000) — the CHECK constraint and this enum are one list.
 */
export const FULFILLMENT_MODELS = Object.freeze({
  /** Order 1 case, pay $118 for the case. What every row assumes today. */
  FIXED_PACKAGE: 'fixed_package',
  /** Order 1 case ≈ 60 LB, billed $16.40 for every pound that arrives. */
  CATCH_WEIGHT: 'catch_weight',
  /** Order 17.75 LB, billed $X/LB. Exact — the vendor cuts to the number. */
  CUT_TO_ORDER: 'cut_to_order',
  /** Order 1 piece weighing about 6–7 LB, billed $X/LB. */
  RANDOM_WEIGHT_PIECE: 'random_weight_piece',
});

/**
 * Not a fulfillment model — the resolver's refusal to name one. Deliberately
 * outside FULFILLMENT_MODELS so it can never be written to the column.
 */
export const UNKNOWN_MODEL = 'unknown';

/** Every model whose cost is an ESTIMATE until the truck is weighed. */
export const ESTIMATED_MODELS = Object.freeze([
  FULFILLMENT_MODELS.CATCH_WEIGHT,
  FULFILLMENT_MODELS.RANDOM_WEIGHT_PIECE,
]);

export const BILLING_BASIS_WARNINGS = Object.freeze({
  /** No option was supplied. */
  NO_OPTION: 'no_option',
  /** The row has no purchase_unit, so nothing says what one order is. */
  MISSING_PURCHASE_UNIT: 'missing_purchase_unit',
  /** No usable price of any kind. */
  MISSING_PRICE: 'missing_price',
  /**
   * `purchase_unit` names a WEIGHT while the pack describes a wrapper holding
   * many of them. The column is answering the billing question, not the order
   * question — the ribeye signature.
   */
  PURCHASE_UNIT_IS_BILLING_UNIT: 'purchase_unit_is_billing_unit',
  /**
   * A weight-priced row whose pack text the parser could not read, so the
   * shipped weight per order unit is unknown. Catch-weight is SUSPECTED and
   * refused: a human confirms it, arithmetic does not.
   */
  CATCH_WEIGHT_SUSPECTED_PACK_UNREADABLE: 'catch_weight_suspected_pack_unreadable',
  /** The stored fulfillment_model is set but its companion columns are not. */
  STORED_MODEL_INCOMPLETE: 'stored_model_incomplete',
  /**
   * The wrapper NOUN ("case") is a display label chosen because the pack names
   * several pieces and no container word. It never enters any arithmetic.
   */
  ORDER_UNIT_LABEL_ASSUMED: 'order_unit_label_assumed',
  /**
   * `package_price` is a per-billing-unit price on this row and must not be
   * multiplied by a package count. It is returned as null for that reason.
   */
  PACKAGE_PRICE_IS_NOT_A_PACKAGE_PRICE: 'package_price_is_not_a_package_price',
  /** The cost this basis produces is an estimate and must be labelled. */
  COST_IS_ESTIMATED: 'cost_is_estimated',
  /** Evidence points two ways; no model asserted. */
  AMBIGUOUS_EVIDENCE: 'ambiguous_evidence',
});

/* ── Small, total helpers ─────────────────────────────────────────────────── */

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toPositiveNumber = (value) => {
  const parsed = toFiniteNumber(value);
  return parsed !== null && parsed > 0 ? parsed : null;
};

const text = (value) => (value === null || value === undefined ? '' : String(value).trim());
const fold = (value) => text(value).toLowerCase().replace(/\s+/g, ' ');
const round2 = (value) => Math.round(value * 100) / 100;
const round6 = (value) => Math.round(value * 1e6) / 1e6;

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

/* ── Unit vocabulary ──────────────────────────────────────────────────────── */
//
// Word CLASSES only — no factors. Every actual conversion goes through
// packageBasis.convertBasisQuantity, so there is still exactly one place in
// this repo where a conversion factor is written down.

const WEIGHT_UNIT_WORDS = new Set([
  'lb', 'lbs', 'pound', 'pounds', '#',
  'kg', 'kgs', 'kilogram', 'kilograms',
  'g', 'gram', 'grams',
  'oz', 'ozs', 'ounce', 'ounces',
]);

const VOLUME_UNIT_WORDS = new Set([
  'ml', 'milliliter', 'milliliters', 'l', 'liter', 'liters', 'litre', 'litres',
  'fl oz', 'floz', 'fluid ounce', 'fluid ounces',
  'qt', 'qts', 'quart', 'quarts', 'gal', 'gallon', 'gallons', 'pt', 'pint', 'pints',
]);

/**
 * Wrapper nouns — a shipping container the vendor hands over as one unit.
 * Mirrors packText.SHIPPING_CONTAINERS in spirit; kept local because this
 * module may not import that file (see the header).
 */
const CONTAINER_UNIT_WORDS = new Set([
  'case', 'cases', 'cs', 'box', 'boxes', 'bx', 'carton', 'cartons', 'ctn',
  'bag', 'bags', 'sack', 'sacks', 'crate', 'crates', 'pail', 'pails',
  'tub', 'tubs', 'tray', 'trays', 'bucket', 'buckets', 'drum', 'drums',
  'jug', 'jugs', 'pack', 'packs', 'pk', 'package', 'packages', 'flat', 'flats',
]);

/** Generic piece words — one countable unit of the product itself. */
const PIECE_UNIT_WORDS = new Set([
  'ea', 'each', 'pc', 'pcs', 'piece', 'pieces', 'ct', 'count',
  'unit', 'units', 'item', 'items', 'side', 'sides',
]);

/**
 * Does the vendor's own wording say this package VARIES?
 *
 * A substring test on the pack string, deriving no quantity from it — "one
 * pack string, one meaning" is about who computes from a pack, and nothing
 * here computes. It answers only: did the supplier write down that the weight
 * is an average, a range, or catch-weight?
 *
 * It is used solely to decide whether an UNREADABLE pack is worth a human's
 * attention. It can never classify a row as catch-weight on its own — that
 * still needs a real approximate total from the parser — so a false positive
 * here costs a line in a report and never a penny on an invoice.
 *
 * Without this test every weight-priced row whose pack is prose ("per lb",
 * "bulk", "house blend", "thin sliced") was reported as a suspected
 * catch-weight row: 77 of them project-wide, of which about ten were real.
 */
const VARIABILITY_MARKER_RE = /(\bavg\b|\baverage\b|\bapprox\b|\bapproximately\b|~|\bcwt\b|\bcatch[\s-]?weight\b|\brandom[\s-]?weight\b|\d\s*(?:-|–|—|\bto\b)\s*\d)/i;

export const packTextClaimsVariableWeight = (packDescription) =>
  VARIABILITY_MARKER_RE.test(text(packDescription));

const unitClass = (unitRaw) => {
  const key = fold(unitRaw);
  if (!key) return 'none';
  if (WEIGHT_UNIT_WORDS.has(key)) return 'weight';
  if (VOLUME_UNIT_WORDS.has(key)) return 'volume';
  if (CONTAINER_UNIT_WORDS.has(key)) return 'container';
  if (PIECE_UNIT_WORDS.has(key)) return 'piece';
  return 'other';
};

/** A unit the vendor can bill a variable amount of — weight or volume. */
const isBillableMeasureUnit = (unitRaw) => {
  const klass = unitClass(unitRaw);
  return klass === 'weight' || klass === 'volume';
};

/* ── Evidence gathering ───────────────────────────────────────────────────── */

/**
 * @typedef {object} PackFacts
 *   The parser's reading of one pack string, in the shape `packText.readPack`
 *   returns. Callers that own the parser may pass it straight through; this
 *   module otherwise reconstructs the same facts from what the sync writers
 *   froze into `conversion_snapshot`.
 * @property {boolean} read
 * @property {number|null} totalQty       total in one vendor pack
 * @property {string|null} unitToken      unit that total is in
 * @property {boolean} approximate        the vendor said AVG / a range
 * @property {number|null} pieceCount     variable-weight pieces in the pack
 * @property {object|null} perPiece       { qty, unit, min, max, approximate }
 * @property {string|null} containerWord  wrapper noun the pack named
 */

const EMPTY_PACK_FACTS = Object.freeze({
  read: false,
  totalQty: null,
  unitToken: null,
  approximate: false,
  pieceCount: null,
  perPiece: null,
  containerWord: null,
  source: 'none',
});

/**
 * Rebuilds pack facts from the snapshot the sync writers already stored.
 *
 * `conversion_snapshot.package_measurements` is a list of simultaneous,
 * complementary facts about one package — a `piece_count` row AND a
 * `vendor_pack` weight row describe the same case from two directions
 * (AGENTS.md, "An explicit pack-text piece count is order-authorizing").
 * `conversion_snapshot.per_piece` is their sibling, not a member.
 *
 * Note this reads `approximate` straight off the stored row. That flag is what
 * separates "a 60 LB case" from "about a 60 LB case", and it is the difference
 * between an exact total and an estimate — `packageBasis.extractPackageMeasurements`
 * drops it, which is why this module reads the raw snapshot rather than
 * borrowing that reader.
 */
function packFactsFromSnapshot(option) {
  const snapshot = isObject(option?.conversion_snapshot) ? option.conversion_snapshot : null;
  if (!snapshot) return EMPTY_PACK_FACTS;

  const rows = Array.isArray(snapshot.package_measurements) ? snapshot.package_measurements : [];
  const perPieceRaw = isObject(snapshot.per_piece) ? snapshot.per_piece : null;

  let totalQty = null;
  let unitToken = null;
  let approximate = false;
  let pieceCount = null;

  for (const row of rows) {
    if (!isObject(row)) continue;
    const quantity = toPositiveNumber(row.quantity);
    if (quantity === null) continue;
    const role = fold(row.role);
    const unit = text(row.unit) || null;

    if (role === 'piece_count') {
      if (pieceCount === null) pieceCount = quantity;
      continue;
    }
    // Any measurable total for the package — the weight/volume side of the
    // same pack. `vendor_pack` is what the deterministic writer uses.
    if (isBillableMeasureUnit(unit) && totalQty === null) {
      totalQty = quantity;
      unitToken = unit;
      if (row.approximate === true) approximate = true;
    }
  }

  const perPiece = perPieceRaw
    ? {
      qty: toPositiveNumber(perPieceRaw.qty),
      unit: text(perPieceRaw.unit) || null,
      min: toPositiveNumber(perPieceRaw.min),
      max: toPositiveNumber(perPieceRaw.max),
      approximate: perPieceRaw.approximate === true,
    }
    : null;

  if (perPiece?.approximate === true) approximate = true;

  const read = totalQty !== null || pieceCount !== null || perPiece?.qty != null;
  if (!read) return EMPTY_PACK_FACTS;

  return {
    read: true,
    totalQty,
    unitToken,
    approximate,
    pieceCount,
    perPiece,
    containerWord: null,
    source: 'package_measurements',
  };
}

/** Normalizes an injected `readPack` result into the same internal shape. */
function normalizeInjectedPackFacts(facts) {
  if (!isObject(facts) || facts.read !== true) return EMPTY_PACK_FACTS;
  const perPieceRaw = isObject(facts.perPiece) ? facts.perPiece : null;
  return {
    read: true,
    totalQty: toPositiveNumber(facts.totalQty),
    unitToken: text(facts.unitToken) || null,
    approximate: facts.approximate === true || perPieceRaw?.approximate === true,
    pieceCount: toPositiveNumber(facts.pieceCount),
    perPiece: perPieceRaw
      ? {
        qty: toPositiveNumber(perPieceRaw.qty),
        unit: text(perPieceRaw.unit) || null,
        min: toPositiveNumber(perPieceRaw.min),
        max: toPositiveNumber(perPieceRaw.max),
        approximate: perPieceRaw.approximate === true,
      }
      : null,
    containerWord: text(facts.containerWord) || null,
    source: 'pack_facts',
  };
}

/**
 * Every fact this resolver is allowed to reason from, gathered once.
 * Exported for the audit script, which reports the evidence beside the verdict
 * so a human reviewing an ambiguous row sees what the classifier saw.
 */
export function collectBillingEvidence(option, { item = null, packFacts = null } = {}) {
  const purchaseUnit = text(option?.purchase_unit) || null;
  const injected = packFacts ? normalizeInjectedPackFacts(packFacts) : null;
  const pack = injected && injected.read ? injected : packFactsFromSnapshot(option);

  return {
    purchaseUnit,
    purchaseUnitClass: unitClass(purchaseUnit),
    packDescription: text(option?.pack_description) || null,
    qtyPerPackage: toPositiveNumber(option?.qty_per_package),
    packagePrice: toPositiveNumber(option?.package_price),
    allowsFractional: option?.allows_fractional_packages === true,
    itemUnit: text(item?.unit ?? item?.count_unit) || null,
    normalizedCountUnit: text(option?.normalized_count_unit) || null,
    storedModel: text(option?.fulfillment_model) || null,
    storedBillingUnit: text(option?.billing_unit) || null,
    storedBillingUnitPrice: toPositiveNumber(option?.billing_unit_price),
    storedBillingQty: toPositiveNumber(option?.estimated_billing_qty_per_order_unit),
    pack,
  };
}

/* ── The resolver ─────────────────────────────────────────────────────────── */

// Every path returns the SAME keys. A caller must never have to check whether
// a field exists before reading it — an absent fact is null, not missing.
const BASIS_SHAPE = Object.freeze({
  model: UNKNOWN_MODEL,
  orderUnit: null,
  packageQuantity: null,
  packageQuantityUnit: null,
  billingUnit: null,
  billingUnitPrice: null,
  estimatedBillingQtyPerOrderUnit: null,
  packagePrice: null,
  estimated: false,
  confidence: 0,
  source: 'none',
  // Descriptive extras, always present: what the pack said the wrapper holds.
  pieceCount: null,
  perPiece: null,
});

const makeBasis = (fields) => Object.freeze({
  ...BASIS_SHAPE,
  ...fields,
  warnings: Object.freeze(fields.warnings || []),
});

const emptyBasis = (warnings) => makeBasis({ warnings });

/**
 * Which of the four models this row follows, and the numbers that model needs.
 *
 * ORDER and BILLING are answered separately and both are always returned:
 *
 *   orderUnit / packageQuantity / packageQuantityUnit
 *       what you tell the vendor, and what one of them contains.
 *   billingUnit / billingUnitPrice / estimatedBillingQtyPerOrderUnit
 *       what the invoice multiplies, at what rate, and how much of it one
 *       order unit is expected to weigh out at.
 *   packagePrice
 *       the price of ONE order unit when the vendor prices it as a fixed
 *       amount — and NULL for catch-weight, where no such number exists.
 *       Returning the per-pound figure here is precisely the production bug.
 *
 * Evidence precedence, strongest first:
 *
 *   1. the explicit billing columns (a stored, reviewed answer);
 *   2. stored pack facts — `conversion_snapshot.package_measurements` /
 *      `per_piece`, or a `packFacts` reading injected by a caller that owns
 *      the parser — read together with the purchase unit;
 *   3. the purchase unit alone, which can only ever mean fixed-package or
 *      cut-to-order, neither of which changes today's arithmetic.
 *
 * Anything that does not settle returns `unknown`, `estimated: false`, and a
 * warning naming what was missing. A model is never inferred from a price
 * looking small: this module has no idea what beef costs, and a threshold that
 * decided otherwise would be a guess wearing arithmetic's clothes.
 *
 * @param {object|null} option  a `purchase_options` row
 * @param {{ item?: object|null, packFacts?: object|null }} [ctx]
 * @returns {{ model: string, orderUnit: string|null, packageQuantity: number|null,
 *   packageQuantityUnit: string|null, billingUnit: string|null,
 *   billingUnitPrice: number|null, estimatedBillingQtyPerOrderUnit: number|null,
 *   packagePrice: number|null, estimated: boolean, confidence: number,
 *   source: string, warnings: string[] }}
 *   Never throws; every field is present on every path.
 */
export function resolveBillingBasis(option, ctx = {}) {
  const W = BILLING_BASIS_WARNINGS;
  if (!isObject(option)) return emptyBasis([W.NO_OPTION]);

  const item = isObject(ctx) ? ctx.item ?? null : null;
  const packFacts = isObject(ctx) ? ctx.packFacts ?? null : null;
  const ev = collectBillingEvidence(option, { item, packFacts });

  // ── 1. An explicit stored answer ─────────────────────────────────────────
  const storedValues = Object.values(FULFILLMENT_MODELS);
  if (ev.storedModel && storedValues.includes(ev.storedModel)) {
    const stored = basisFromStoredColumns(ev);
    if (stored) return stored;
    // The column says catch-weight but the numbers it needs are absent. Do not
    // fall through to a guess — an unfinished migration is not evidence.
    return emptyBasis([W.STORED_MODEL_INCOMPLETE]);
  }

  if (!ev.purchaseUnit) return emptyBasis([W.MISSING_PURCHASE_UNIT]);

  // ── 2. Pack facts read together with the purchase unit ───────────────────
  const measured = basisFromPackEvidence(ev);
  if (measured) return measured;

  // ── 3. The purchase unit alone ───────────────────────────────────────────
  return basisFromPurchaseUnitAlone(ev);
}

/** Precedence 1 — the row already carries a reviewed answer. */
function basisFromStoredColumns(ev) {
  const W = BILLING_BASIS_WARNINGS;
  const model = ev.storedModel;
  const warnings = [];

  if (model === FULFILLMENT_MODELS.CATCH_WEIGHT
    || model === FULFILLMENT_MODELS.RANDOM_WEIGHT_PIECE) {
    if (!ev.storedBillingUnit || ev.storedBillingUnitPrice === null
      || ev.storedBillingQty === null) {
      return null;
    }
    const orderUnit = orderUnitLabelFor(ev, model, warnings);
    warnings.push(W.PACKAGE_PRICE_IS_NOT_A_PACKAGE_PRICE, W.COST_IS_ESTIMATED);
    return makeBasis({
      model,
      orderUnit,
      packageQuantity: ev.storedBillingQty,
      packageQuantityUnit: ev.storedBillingUnit,
      billingUnit: ev.storedBillingUnit,
      billingUnitPrice: ev.storedBillingUnitPrice,
      estimatedBillingQtyPerOrderUnit: ev.storedBillingQty,
      packagePrice: null,
      estimated: true,
      confidence: 1,
      source: 'stored_columns',
      warnings,
    });
  }

  // fixed_package / cut_to_order — exact, billed in the order unit itself.
  const price = ev.storedBillingUnitPrice ?? ev.packagePrice;
  if (price === null) warnings.push(W.MISSING_PRICE);
  const unit = ev.storedBillingUnit ?? ev.purchaseUnit;
  return makeBasis({
    model,
    orderUnit: ev.purchaseUnit,
    packageQuantity: ev.qtyPerPackage,
    packageQuantityUnit: ev.itemUnit ?? ev.normalizedCountUnit ?? null,
    billingUnit: unit,
    billingUnitPrice: price,
    estimatedBillingQtyPerOrderUnit: ev.storedBillingQty ?? 1,
    packagePrice: price,
    estimated: false,
    confidence: 1,
    source: 'stored_columns',
    warnings,
  });
}

/**
 * Precedence 2 — the decisive reading.
 *
 * The signature that makes a row catch-weight is not a price and not a word in
 * isolation. It is a CONTRADICTION between two stored facts: `purchase_unit`
 * names a weight, and the pack says one vendor pack holds appreciably MORE
 * than one of that weight, approximately. A pound cannot contain sixty pounds.
 * The only coherent reading is that `purchase_unit` was answering the BILLING
 * question, and the order unit is the wrapper the pack describes.
 *
 * That single rule catches both live corruption shapes — store 373's
 * `qty_per_package = 1` and store 383's `qty_per_package = 60` — because
 * neither is consulted. It is the pack, not the stored quantity, that proves
 * the purchase unit cannot be the order unit.
 */
function basisFromPackEvidence(ev) {
  const W = BILLING_BASIS_WARNINGS;
  const pack = ev.pack;

  // Only a weight/volume-priced row can be mis-describing its order unit. A
  // row that orders in `case` already orders in a wrapper; whatever its pack
  // says, `packages × package_price` is the invoice.
  if (ev.purchaseUnitClass !== 'weight' && ev.purchaseUnitClass !== 'volume') return null;

  if (!pack.read) {
    // A weight-priced row whose pack could not be read. Only worth raising
    // when the vendor's own wording says the weight VARIES — "AVG 6–7 LB / PC",
    // "10 LB / CWT". A pack that is merely prose ("per lb", "bulk") says
    // nothing about catch-weight, so it falls through to the ordinary
    // weight-priced reading and keeps today's arithmetic exactly.
    if (packTextClaimsVariableWeight(ev.packDescription)) {
      return emptyBasis([W.CATCH_WEIGHT_SUSPECTED_PACK_UNREADABLE]);
    }
    return null;
  }

  const totalInPurchaseUnit = pack.totalQty !== null
    ? convertBasisQuantity(pack.totalQty, pack.unitToken, ev.purchaseUnit)
    : null;

  // The pack's total must be expressible in the billing unit, and must exceed
  // one of it by a real margin, or there is no contradiction to resolve.
  if (totalInPurchaseUnit === null || totalInPurchaseUnit <= 1 + 1e-9) return null;

  // An EXACT total that exceeds one purchase unit is a different defect — a
  // 50 LB sack whose purchase_unit says LB. That is the existing
  // `purchase_unit_pack_conflict`, already audited and repaired elsewhere, and
  // it is not catch-weight: nothing about it is variable. Leave it alone.
  if (!pack.approximate) return emptyBasis([W.AMBIGUOUS_EVIDENCE]);

  if (ev.packagePrice === null) return emptyBasis([W.MISSING_PRICE]);

  const pieces = pack.pieceCount;
  const isMultiPiece = pieces !== null && pieces >= 2;
  const model = isMultiPiece || pack.containerWord
    ? FULFILLMENT_MODELS.CATCH_WEIGHT
    : FULFILLMENT_MODELS.RANDOM_WEIGHT_PIECE;

  const warnings = [W.PURCHASE_UNIT_IS_BILLING_UNIT, W.PACKAGE_PRICE_IS_NOT_A_PACKAGE_PRICE];
  const orderUnit = orderUnitLabelFor(ev, model, warnings);
  warnings.push(W.COST_IS_ESTIMATED);

  const estQty = round6(totalInPurchaseUnit);

  return makeBasis({
    model,
    orderUnit,
    packageQuantity: estQty,
    packageQuantityUnit: ev.purchaseUnit,
    billingUnit: ev.purchaseUnit,
    // The catalog's per-unit price, correctly named at last.
    billingUnitPrice: ev.packagePrice,
    estimatedBillingQtyPerOrderUnit: estQty,
    // There is no fixed price for one order unit. Saying so is the point.
    packagePrice: null,
    estimated: true,
    // Deterministic pack arithmetic, but the WEIGHT is the vendor's average.
    confidence: 0.9,
    source: pack.source,
    warnings,
    pieceCount: pieces,
    perPiece: pack.perPiece,
  });
}

/**
 * Precedence 3 — nothing but the purchase unit.
 *
 * Both outcomes here compute `packages × package_price`, which is exactly what
 * every caller already did. This branch cannot change a number; it only gives
 * today's behaviour a name.
 */
function basisFromPurchaseUnitAlone(ev) {
  const W = BILLING_BASIS_WARNINGS;
  const warnings = [];
  if (ev.packagePrice === null) warnings.push(W.MISSING_PRICE);

  const billedByMeasure = ev.purchaseUnitClass === 'weight' || ev.purchaseUnitClass === 'volume';
  // A weight-priced row with no contradicting pack IS cut-to-order: you ask
  // for 17.75 LB and are billed for 17.75 LB. Exact, and arithmetically
  // identical to a fixed package of one.
  const model = billedByMeasure && (ev.allowsFractional || ev.qtyPerPackage === null || ev.qtyPerPackage === 1)
    ? FULFILLMENT_MODELS.CUT_TO_ORDER
    : FULFILLMENT_MODELS.FIXED_PACKAGE;

  return makeBasis({
    model,
    orderUnit: ev.purchaseUnit,
    packageQuantity: ev.qtyPerPackage,
    packageQuantityUnit: ev.itemUnit ?? ev.normalizedCountUnit ?? null,
    billingUnit: ev.purchaseUnit,
    billingUnitPrice: ev.packagePrice,
    estimatedBillingQtyPerOrderUnit: 1,
    packagePrice: ev.packagePrice,
    estimated: false,
    confidence: model === FULFILLMENT_MODELS.CUT_TO_ORDER ? 0.7 : 0.6,
    source: 'purchase_unit',
    warnings,
  });
}

/**
 * The NOUN a catch-weight order is counted in.
 *
 * The pack's own container word when it named one. Otherwise "case" for a
 * multi-piece wrapper and "piece" for a single variable-weight item — a
 * DISPLAY label, flagged as assumed, that no arithmetic anywhere reads. The
 * quantities are evidence; this word is only how the order reads aloud.
 */
function orderUnitLabelFor(ev, model, warnings) {
  const fromPack = text(ev.pack?.containerWord);
  if (fromPack) return fromPack;
  if (ev.purchaseUnitClass === 'container' || ev.purchaseUnitClass === 'piece') {
    return ev.purchaseUnit;
  }
  warnings.push(BILLING_BASIS_WARNINGS.ORDER_UNIT_LABEL_ASSUMED);
  return model === FULFILLMENT_MODELS.RANDOM_WEIGHT_PIECE ? 'piece' : 'case';
}

/* ── Money ────────────────────────────────────────────────────────────────── */

/**
 * What `packages` order units are expected to cost, and whether that number is
 * knowable in advance.
 *
 * For a fixed package or a cut-to-order weight the answer is exact and is the
 * same `packages × price` every caller already computed. For catch-weight and
 * random-weight it is `packages × estimatedBillingQtyPerOrderUnit ×
 * billingUnitPrice` and it is an ESTIMATE — the real invoice is settled on the
 * scale. `packages × billingUnitPrice` is never returned as exact for those
 * models; presenting the per-pound rate as a line total is the bug this whole
 * module exists to end.
 *
 * @param {object|null} basis   a resolveBillingBasis result
 * @param {number} packages     order units being bought
 * @returns {{ amount: number|null, estimated: boolean, basisText: string|null }}
 *   `amount` is null when the basis cannot price anything. Never throws.
 */
export function estimateOrderCost(basis, packages) {
  const none = { amount: null, estimated: false, basisText: null };
  if (!isObject(basis)) return none;

  const count = toFiniteNumber(packages);
  if (count === null || count < 0) return none;

  const estimated = ESTIMATED_MODELS.includes(basis.model);

  if (estimated) {
    const rate = toPositiveNumber(basis.billingUnitPrice);
    const per = toPositiveNumber(basis.estimatedBillingQtyPerOrderUnit);
    if (rate === null || per === null) return { ...none, estimated: true };
    return {
      amount: round2(count * per * rate),
      estimated: true,
      basisText: `${formatMoney(rate)}/${basis.billingUnit ?? ''}`.trim()
        + ` × ~${formatQuantity(per)}${basis.billingUnit ? ` ${basis.billingUnit}` : ''}`,
    };
  }

  const price = toPositiveNumber(basis.packagePrice) ?? toPositiveNumber(basis.billingUnitPrice);
  if (price === null) return none;
  return {
    amount: round2(count * price),
    estimated: false,
    basisText: basis.orderUnit ? `${formatMoney(price)}/${basis.orderUnit}` : formatMoney(price),
  };
}

/**
 * The one seam the order-math modules use.
 *
 * Returns a replacement extended cost ONLY when the vendor genuinely settles
 * this line on a scale and the numbers to estimate it are all present.
 * Otherwise it returns null and the caller's existing arithmetic runs
 * untouched — which is what makes "fixed-package behaviour is byte-identical"
 * true by inspection rather than by hope: there is exactly one `if` between
 * today's code and this module, and a null takes the old branch.
 *
 * A row that is merely SUSPECTED of being catch-weight also returns null. Its
 * cost stays exactly what it is today; changing a price on a suspicion would
 * be the same class of mistake in the opposite direction. `needsReview` on the
 * resolved basis is how a caller labels it instead.
 *
 * @param {object|null} option
 * @param {number|null} packages
 * @param {{ item?: object|null, packFacts?: object|null }} [ctx]
 * @returns {{ amount: number, basisText: string, model: string,
 *             billingUnit: string|null, billingUnitPrice: number|null,
 *             estimatedBillingQtyPerOrderUnit: number|null } | null}
 */
export function estimatedExtendedCost(option, packages, ctx = {}) {
  const basis = resolveBillingBasis(option, ctx);
  if (!ESTIMATED_MODELS.includes(basis.model)) return null;
  const cost = estimateOrderCost(basis, packages);
  if (cost.amount === null || cost.estimated !== true) return null;
  return {
    amount: cost.amount,
    basisText: cost.basisText,
    model: basis.model,
    billingUnit: basis.billingUnit,
    billingUnitPrice: basis.billingUnitPrice,
    estimatedBillingQtyPerOrderUnit: basis.estimatedBillingQtyPerOrderUnit,
  };
}

/**
 * True when a row's pack says its weight varies but nothing stored can say by
 * how much — so its money is presented unchanged and marked as unconfirmed.
 * `scripts/audit-catch-weight.mjs` lists these; nothing repairs them silently.
 */
export function billingBasisNeedsReview(basis) {
  return isObject(basis)
    && Array.isArray(basis.warnings)
    && basis.warnings.includes(BILLING_BASIS_WARNINGS.CATCH_WEIGHT_SUSPECTED_PACK_UNREADABLE);
}

/* ── Classification persistence planner ───────────────────────────────────── */

/**
 * The purchase_options patch that makes a decisively evidence-classified
 * variable-weight row EXPLICIT — the same column shape the legacy repair
 * migration (20260814160200) and the audit's human-reviewed SQL
 * (`scripts/audit-catch-weight.mjs` buildCatchWeightRepair) write, produced
 * here so the ongoing pipeline and the one-time repairs can never drift.
 *
 * Why this exists: the browser already PRICES by the evidence classification
 * (estimateOrderCost via basisFromPackEvidence — the Savings Review's ~$984
 * ribeye estimate), while the database's arithmetic
 * (purchase_option_billing_economics) reads only the STORED billing columns
 * and treats NULL fulfillment_model as fixed-package. One classifier with two
 * consumers reading different state is how an approved ~$296 review line
 * became a $9.25 PO line. Persisting the verdict the browser already acts on
 * closes the split; the freeze trigger's cost-drift refusal backstops any
 * residue.
 *
 * This module still decides nothing about writing (see the header). The
 * planner only fires on the DECISIVE signature — a weight/volume purchase
 * unit contradicted by a parser-read, vendor-marked-approximate pack total
 * (resolveBillingBasis confidence 0.9) — and the caller must apply the patch
 * with a guard equivalent to `WHERE fulfillment_model IS NULL AND status =
 * 'active' AND package_price = <the price this classification read>`, so a
 * concurrent human decision or price edit always wins.
 *
 * Deliberate refusals, all returning null:
 *   - anything already classified (fulfillment_model set) or inactive;
 *   - fixed-package / cut-to-order rows (nothing to persist — NULL already
 *     means that);
 *   - suspected-but-unreadable packs (billingBasisNeedsReview — a human calls
 *     those, never arithmetic);
 *   - human-confirmed exact packages (a confirmed measurement carries no
 *     `approximate`, so the contradiction the classifier needs never forms).
 *
 * The order-unit normalization (purchase_unit → wrapper label,
 * qty_per_package → expected billing quantity, conversion snapshot inputs)
 * is included only when the item itself counts in the billing unit's own
 * family — the shape the repair migration wrote for LB-counted meat. An item
 * counted in EA keeps its piece-count package math untouched and gains only
 * the billing columns.
 *
 * @param {object|null} option a purchase_options row
 * @param {{ item?: object|null, packFacts?: object|null }} [ctx]
 * @returns {{ optionId: any, patch: object, basis: object,
 *             guard: { packagePrice: number } } | null}
 */
export function planBillingBasisClassification(option, ctx = {}) {
  if (!isObject(option) || option.id == null) return null;
  if (option.status !== 'active') return null;
  if (text(option.fulfillment_model)) return null;
  const packagePrice = toPositiveNumber(option.package_price);
  if (packagePrice === null) return null;

  const basis = resolveBillingBasis(option, ctx);
  if (!ESTIMATED_MODELS.includes(basis.model)) return null;
  if (billingBasisNeedsReview(basis)) return null;
  if (basis.source !== 'package_measurements' && basis.source !== 'pack_facts') return null;
  if (!(basis.confidence >= 0.9)) return null;

  const rate = toPositiveNumber(basis.billingUnitPrice);
  const billingQty = toPositiveNumber(basis.estimatedBillingQtyPerOrderUnit);
  const billingUnit = text(basis.billingUnit) || null;
  if (rate === null || billingQty === null || !billingUnit) return null;

  const item = isObject(ctx) ? ctx.item ?? null : null;
  const itemUnit = text(item?.unit ?? item?.count_unit) || null;
  const itemCountsInBillingFamily = itemUnit !== null
    && convertBasisQuantity(1, itemUnit, billingUnit) !== null;

  const snapshot = isObject(option.conversion_snapshot) ? { ...option.conversion_snapshot } : {};
  delete snapshot.price_comparison;
  snapshot.billing_basis = {
    fulfillment_model: basis.model,
    order_unit: basis.orderUnit,
    billing_unit: billingUnit,
    billing_unit_price: rate,
    estimated_billing_qty_per_order_unit: billingQty,
    cost_is_estimated: true,
    source: 'explicit_pack_text',
  };

  const patch = {
    fulfillment_model: basis.model,
    billing_unit: billingUnit,
    billing_unit_price: rate,
    estimated_billing_qty_per_order_unit: billingQty,
    package_price: null,
    // The comparison price of a variable-weight row IS its billing rate. Left
    // as package_price ÷ stored quantity it is either the misfiled rate again
    // or the $0.27/LB prime-ribeye poison that wins every comparison.
    normalized_unit_cost: rate,
    normalized_count_unit: billingUnit,
    conversion_snapshot: snapshot,
  };

  if (itemCountsInBillingFamily) {
    patch.purchase_unit = basis.orderUnit;
    patch.qty_per_package = billingQty;
    patch.conversion_status = 'valid';
    patch.conversion_provenance = {
      ...(isObject(option.conversion_provenance) ? option.conversion_provenance : {}),
      engine: 'packText+billingBasis',
      method: 'pack_parsed',
      evidence: 'explicit_pack_text',
      order_safe: true,
    };
    snapshot.qty = billingQty;
    snapshot.status = 'valid';
    snapshot.reason = null;
    snapshot.evidence = 'explicit_pack_text';
    snapshot.order_safe = true;
    snapshot.inputs = {
      ...(isObject(snapshot.inputs) ? snapshot.inputs : {}),
      countUnit: billingUnit,
      purchaseUnit: basis.orderUnit,
      caseQuantity: billingQty,
      packDescription: text(option.pack_description) || null,
    };
  }

  return {
    optionId: option.id,
    patch,
    basis,
    guard: { packagePrice },
  };
}

/* ── Formatting ───────────────────────────────────────────────────────────── */
//
// Deliberately not Intl: this text is asserted character-for-character by
// tests and rendered by two runtimes, and Intl's grouping and minus signs are
// not identical across them.

/** `1234.5` → `"$1,234.50"`. */
export function formatMoney(value) {
  const amount = toFiniteNumber(value);
  if (amount === null) return '';
  const negative = amount < 0;
  const fixed = Math.abs(amount).toFixed(2);
  const [whole, cents] = fixed.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}$${grouped}.${cents}`;
}

/** `60` → `"60"`, `14.7` → `"14.7"`, `3.14159` → `"3.142"`. */
export function formatQuantity(value) {
  const amount = toFiniteNumber(value);
  if (amount === null) return '';
  const rounded = Math.round(amount * 1000) / 1000;
  if (Number.isInteger(rounded)) return String(rounded);
  return String(rounded);
}

const NEVER_PLURALIZED = new Set([
  ...WEIGHT_UNIT_WORDS, ...VOLUME_UNIT_WORDS, 'each',
]);

const HUMAN_COUNT_UNIT_WORDS = new Map([
  ['pk', 'pack'], ['pkg', 'pack'], ['pack', 'pack'],
  ['cs', 'case'], ['case', 'case'],
  ['ea', 'each'], ['each', 'each'],
]);

/** "1 case" / "2 cases" / "3 LB" — unit symbols never take an `s`. */
export function formatUnitCount(count, unit) {
  const qty = toFiniteNumber(count);
  const rawWord = text(unit);
  const word = HUMAN_COUNT_UNIT_WORDS.get(fold(rawWord)) || rawWord;
  if (qty === null) return word;
  if (!word) return formatQuantity(qty);
  const one = Math.abs(qty - 1) < 1e-9;
  if (one || NEVER_PLURALIZED.has(fold(word))) return `${formatQuantity(qty)} ${word}`;
  const plural = /(s|x|z|ch|sh)$/i.test(word) ? `${word}es` : `${word}s`;
  return `${formatQuantity(qty)} ${plural}`;
}

/* ── Display model ────────────────────────────────────────────────────────── */

/**
 * The buy instruction for one review line, as strings, with no opinion about
 * where they are rendered.
 *
 * PURE. It imports no React, touches no DOM, reads no globals and never
 * throws, so the Savings Review worksheet, the draft PO, a vendor email and a
 * test can all render the same sentence from the same facts. Every field is a
 * string or null; a caller may show any subset in any order.
 *
 * ── Contract ────────────────────────────────────────────────────────────────
 *
 * @param {object} args
 * @param {object|null} args.line   the review/allocation line. Read (all
 *   optional): `quantity_needed` | `needQty`, `count_unit` | `countUnit`,
 *   `packages_required` | `packages`, `quantity_received` | `quantityReceived`,
 *   `overage`.
 * @param {object|null} args.option the `purchase_options` row. Read only for
 *   `pack_description` when the basis carries no pack text of its own.
 * @param {object|null} args.basis  a `resolveBillingBasis` result. When absent
 *   or `unknown`, every money-bearing field comes back null rather than
 *   falling back to a number that would read as exact.
 *
 * @returns {{ buyText: string|null, packText: string|null, needText: string|null,
 *   receiveText: string|null, extraText: string|null, priceBasisText: string|null,
 *   estimatedTotalText: string|null, isEstimated: boolean }}
 *
 *   buyText             what to order: "1 case", "18 LB", "2 cases".
 *   packText            what one of them is: "4 pieces · avg ~60 LB total".
 *   needText            the shortage being covered: "23 LB".
 *   receiveText         what turns up: "~60 LB" (the "~" only when estimated).
 *   extraText           what is left over: "~37 LB".
 *   priceBasisText      the rate being charged: "$16.40/LB".
 *   estimatedTotalText  the line total: "~$984.00", "~" only when estimated.
 *   isEstimated         true when the vendor settles this line on a scale.
 *                       A caller showing an estimated total MUST label it;
 *                       nothing here hides that an invoice may differ.
 *
 * ── Worked example — the row that started this ──────────────────────────────
 *
 *   option  purchase_unit 'LB' · '4 PC / AVG 60 LB' · package_price 16.40
 *   line    need 23 LB, 1 package
 *
 *   { buyText: '1 case',
 *     packText: '4 pieces · avg ~60 LB total',
 *     needText: '23 LB',
 *     receiveText: '~60 LB',
 *     extraText: '~37 LB',
 *     priceBasisText: '$16.40/LB',
 *     estimatedTotalText: '~$984.00',
 *     isEstimated: true }
 *
 * Never "QTY 1 LB · TOTAL $16.40".
 */
export function buildBuyDisplay({ line = null, option = null, basis = null } = {}) {
  const empty = {
    buyText: null,
    packText: null,
    needText: null,
    receiveText: null,
    extraText: null,
    priceBasisText: null,
    estimatedTotalText: null,
    isEstimated: false,
  };

  const b = isObject(basis) ? basis : null;
  const l = isObject(line) ? line : {};
  const o = isObject(option) ? option : {};

  const isEstimated = b ? ESTIMATED_MODELS.includes(b.model) : false;
  const approx = (value) => (isEstimated && value ? `~${value}` : value || null);

  const packages = toFiniteNumber(l.packages_required ?? l.packages);
  const needQty = toFiniteNumber(l.quantity_needed ?? l.needQty);
  const needUnit = text(l.count_unit ?? l.countUnit) || b?.billingUnit || null;

  // ── What to order ────────────────────────────────────────────────────────
  //
  // An `unknown` basis gets NO buy instruction. The column a fallback would
  // read (`purchase_unit`) is the one under suspicion, so "1 LB" here would
  // reprint the original defect in new type. Null sends the caller to its
  // existing "Package needs confirmation" repair path instead.
  const knownModel = b !== null && b.model !== UNKNOWN_MODEL;
  const orderUnit = knownModel ? b.orderUnit : null;
  const buyText = packages !== null && orderUnit ? formatUnitCount(packages, orderUnit) : null;

  // ── What one of them is ──────────────────────────────────────────────────
  let packText = null;
  if (b && isEstimated) {
    const parts = [];
    const pieces = toPositiveNumber(b.pieceCount);
    // A count only earns its place when there is more than one thing to count.
    if (pieces !== null && pieces >= 2) parts.push(formatUnitCount(pieces, 'piece'));
    const per = toPositiveNumber(b.estimatedBillingQtyPerOrderUnit);
    if (per !== null && b.billingUnit) {
      const total = parts.length ? ' total' : '';
      parts.push(`avg ~${formatQuantity(per)} ${b.billingUnit}${total}`);
    }
    packText = parts.length ? parts.join(' · ') : (text(o.pack_description) || null);
  } else {
    packText = text(o.pack_description) || null;
  }

  // ── Need, receive, extra ─────────────────────────────────────────────────
  const needText = needQty !== null && needUnit
    ? formatUnitCount(needQty, needUnit)
    : null;

  // Receive comes from the line's own package math when it has it — that
  // arithmetic is untouched by this module — and from the basis only when the
  // line could not compute it, which is exactly the broken-row case.
  let receiveQty = toFiniteNumber(l.quantity_received ?? l.quantityReceived);
  let receiveUnit = needUnit;
  if (receiveQty === null && b && packages !== null) {
    const per = toPositiveNumber(b.estimatedBillingQtyPerOrderUnit);
    if (per !== null) {
      receiveQty = round6(packages * per);
      receiveUnit = b.billingUnit ?? needUnit;
    }
  }
  const receiveText = receiveQty !== null && receiveUnit
    ? approx(formatUnitCount(receiveQty, receiveUnit))
    : null;

  let extraQty = toFiniteNumber(l.overage);
  if (extraQty === null && receiveQty !== null && needQty !== null) {
    extraQty = round6(receiveQty - needQty);
  }
  const extraText = extraQty !== null && receiveUnit && extraQty > 0
    ? approx(formatUnitCount(extraQty, receiveUnit))
    : null;

  // ── Money ────────────────────────────────────────────────────────────────
  if (!b || b.model === UNKNOWN_MODEL) {
    return { ...empty, buyText, packText, needText, receiveText, extraText, isEstimated: false };
  }

  const rate = toPositiveNumber(b.billingUnitPrice) ?? toPositiveNumber(b.packagePrice);
  const priceBasisText = rate !== null
    ? `${formatMoney(rate)}/${isEstimated ? (b.billingUnit ?? '') : (b.orderUnit ?? '')}`.replace(/\/$/, '')
    : null;

  const cost = estimateOrderCost(b, packages ?? 0);
  const estimatedTotalText = cost.amount !== null && packages !== null
    ? approx(formatMoney(cost.amount))
    : null;

  return {
    buyText,
    packText,
    needText,
    receiveText,
    extraText,
    priceBasisText,
    estimatedTotalText,
    isEstimated,
  };
}
